<template>
  <v-container fluid>
    <v-row class="mb-4" v-if="!autosave">
      <v-col cols="12">
        <h2 v-if="userData">
          {{ $t("basics.edit", { type: $t("basics.user") }) }}
        </h2>
        <h2 v-else>
          {{ $t("basics.new", { type: $t("basics.user") }) }}
        </h2>
      </v-col>
    </v-row>

    <v-form ref="userEditForm">
      <v-row>
        <v-col>
          <p :style="{ 'font-size': autosave ? 'x-large' : 'unset' }">
            {{ $t("basics.baseData") }}
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-checkbox class="hideDetails" :label="$t('messages.isCompanyAccount')" :readonly="readonly"
            v-model="autosaveFields.is_company_account" @update:modelValue="saveFields">
          </v-checkbox>
        </v-col>
      </v-row>

      <v-row>
        <v-col :cols="12" v-if="autosaveFields.is_company_account">
          <v-text-field variant="outlined"
            :rules="autosaveFields.is_company_account ? [requiredRule, max256] : [max256]"
            v-model="autosaveFields.company_name" :label="$t('basics.company') + '*'" :readonly="readonly"
            @blur="saveFields"></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col :cols="smallCols">
          <v-text-field variant="outlined" :rules="[max256]" v-model="autosaveFields.first_name"
            :label="$t('basics.firstName')" :readonly="readonly" @blur="saveFields"></v-text-field>
        </v-col>
        <v-col :cols="smallCols">
          <v-text-field variant="outlined" :rules="[requiredRule, max256]" v-model="autosaveFields.last_name"
            :label="$t('basics.lastName') + '*'" :readonly="readonly" @blur="saveFields"></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col :cols="!readonly && userData && userData.email != undefined ? undefined : smallCols">
          <v-text-field variant="outlined" :rules="[max256, validEmailRule, requiredRule]"
            v-model="autosaveFields.email" :label="$t('basics.email') + '*'"
            :readonly="readonly || userData && !$checkFunctionPermission(['full_user_patch']) && userData.email != undefined"
            @blur="saveFields"></v-text-field>
        </v-col>
        <v-col cols="auto" v-if="!readonly && userData && userData.email != undefined"
          style="display: flex; align-items: center;" @click="showEditEmail">
          <v-icon class="mb-6" color="primary">
            mdi-pencil
          </v-icon>
        </v-col>


        <v-col :cols="smallCols">
          <v-text-field variant="outlined" :rules="[max20]" v-model="autosaveFields.phone"
            :label="$t('basics.phone') + '*'" :readonly="readonly" @blur="saveFields"></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col :cols="smallCols">
          <v-text-field v-model="autosaveFields.birthday" :label="$t('basics.birthday')" variant="outlined" type="date"
            placeholder="DD.MM.YYYY" :rules="[]" @blur="saveFields" @update:model-value="saveFields"></v-text-field>
        </v-col>
        <v-col :cols="smallCols">
          <v-select :items="genders" v-model="autosaveFields.gender" :label="$t('basics.gender') + '*'"
            :rules="[requiredRule]" :item-title="(item) => $t('basics.' + item.name)" return-object item-value="key"
            variant="outlined" @update:modelValue="saveGender" :readonly="readonly"></v-select>
        </v-col>
      </v-row>

      <v-divider v-if="$checkFunctionPermission(['full_user_patch'])" class="mb-4" />

      <v-row v-if="$checkFunctionPermission(['full_user_patch'])">
        <v-col cols="auto">
          <p :style="{ 'font-size': autosave ? 'x-large' : 'unset' }">{{ $t("basics.bill") }}
          </p>
        </v-col>

        <v-col cols="auto" @click="showBillInfo = !showBillInfo" style="display: flex; align-items: center;">
          <v-icon class="mt-1" v-if="!showBillInfo">mdi-information-outline</v-icon>
          <v-icon class="mt-1" v-else color="primary">mdi-information</v-icon>
        </v-col>
      </v-row>

      <v-row v-if="$checkFunctionPermission(['full_user_patch']) && showBillInfo">
        <v-col>
          <v-card flat style="background-color: rgb(var(--v-theme-background));">
            <v-container flui>
              <v-row>
                <v-col>
                  <b>{{ $t('basics.activePatient') }}</b>: {{ $t('messages.activePatientExplanation') }}
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <b>{{ $t('basics.privateInsurance') }}</b>: {{ $t('messages.privateInsuranceExplanation') }}
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <b>{{ $t('basics.soloBill') }}</b>: {{ $t('messages.soloBillExplanation') }}
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <b>{{ $t('basics.paidBy') }}</b>: {{ $t('messages.paidByExplanation') }}
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>


      <v-row v-if="isPatient && $checkFunctionPermission(['full_user_patch'])">
        <v-col :cols="isMobile ? 12 : 4">
          <v-checkbox v-model="autosaveFields.active_patient" :label="$t('basics.activePatient')"
            @update:modelValue="saveFields"></v-checkbox>
        </v-col>

        <v-col :cols="isMobile ? 12 : 4">
          <v-checkbox :disabled="autosaveFields && autosaveFields.paid_by" class="hideDetails"
            :label="$t('basics.privateInsurance')" :readonly="readonly" v-model="autosaveFields.private_insurance"
            @update:modelValue="saveFields">
          </v-checkbox>
        </v-col>

        <v-col :cols="isMobile ? 12 : 4">
          <v-checkbox :disabled="autosaveFields && autosaveFields.paid_by" class="hideDetails"
            :label="$t('basics.soloBill')" :readonly="readonly" v-model="autosaveFields.solo_bill"
            @update:modelValue="saveFields">
          </v-checkbox>
        </v-col>
      </v-row>

      <v-row v-if="$checkFunctionPermission(['full_user_patch'])">
        <v-col :cols="autosaveFields && autosaveFields.paid_by ? (bigCols - 1) : bigCols">
          <v-combobox :readonly="readonly" :label="$t('basics.paidBy')" :items="availUsers" 
            @update:model-value="savePaidBy" v-model="autosaveFields.paid_by" :rules="[validObject]" variant="outlined"
            flat return-object @update:search="fetchUsers" no-filter autocomplete="off"
            :item-title="(item) => item.full_name" :item-value="(item) => item.full_name">
          </v-combobox>
        </v-col>
        <v-col v-if="autosaveFields && autosaveFields.paid_by" cols="auto" class="mt-3"
          @click="gotoUser($event, autosaveFields.paid_by, false)"
          @mousedown.middle="gotoUser($event, autosaveFields.paid_by, true)">
          <v-tooltip :text="$t('basics.goto', { type: $t('basics.user') })">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props">mdi-account</v-icon>
            </template>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-divider class="mb-4" />

      <v-row v-if="(autosave || !userData) && $checkFunctionPermission(['patch_user_permissions'])">
        <v-col>
          <p :style="{ 'font-size': autosave ? 'x-large' : 'unset' }">
            {{ $t("basics.systemData") }}
          </p>
        </v-col>
      </v-row>

      <v-row v-if="$checkFunctionPermission(['patch_user_permissions'])">
        <v-col :cols="smallCols">
          <v-select variant="outlined" :items="roles" :item-title="(item) => $t('constants.' + item.name)"
            item-value="key" :rules="[requiredArrayRule]" v-model="autosaveFields.permissions" multiple return-object
            chips closable-chips :label="$t('basics.role') + '*'" :readonly="readonly"
            @update:modelValue="savePermissions">
          </v-select>
        </v-col>

        <v-col :cols="smallCols">
          <v-select variant="outlined" :items="therapists" item-title="full_name" :item-value="(item) => item.full_name"
            :rules="[validObjects]" v-model="autosaveFields.therapists" multiple return-object chips closable-chips
            :label="$t('basics.therapists')" :readonly="readonly" @update:modelValue="saveTherapists">
          </v-select>
        </v-col>
      </v-row>

      <v-divider class="mb-2" v-if="autosave && $checkFunctionPermission(['patch_user_permissions'])" />

      <v-row class="mt-1">
        <v-col>
          <p :style="{ 'font-size': autosave ? 'x-large' : 'unset' }">
            {{ $t("basics.homeAddress") }}
          </p>
        </v-col>
      </v-row>

      <AddressComponentVue v-if="autosaveFields.address" :readonly="readonly" :isBirthplace="false"
        :required="this.userData == undefined" :addressData="autosaveFields.address" :cardMode="autosave"
        :autosave="true" @showEdit="showEditAddress" />

      <v-row v-if="isTherapist" :class="{ 'mt-4': autosave }">
        <v-col cols="auto">
          <color-picker v-model:pureColor="hexColor" @pureColorChange="updateColorCode" />
        </v-col>
        <v-col>
          {{ $t('basics.calendarColorCode') }}
        </v-col>
      </v-row>

      <v-row v-if="$checkFunctionPermission(['full_user_patch'])" :class="{ 'mt-4': autosave }">
        <v-col :cols="undefined">
          <v-text-field v-model="autosaveFields.comment_internal" :label="$t('basics.comment')" variant="outlined" flat
            :readonly="readonly" @blur="saveFields" hide-details></v-text-field>
        </v-col>
      </v-row>

      <v-row v-if="isTherapist && $store.state.id == userData.id" :class="{ 'mt-4': autosave }">
        <v-col :cols="undefined">
          <v-text-field v-model="autosaveFields.ical_url" :label="$t('basics.ical')" variant="outlined" flat
            :readonly="readonly" @blur="saveFields" hide-details></v-text-field>
        </v-col>

        <v-col :cols="isMobile ? 12 : 'auto'" style="display: flex; align-items: center;"
          v-if="autosaveFields.ical_url">
          <v-btn :loading="loading" large block flat class="primaryButton" style="width: 100%" @click="showICalWarning">
            <v-icon class="mr-2">mdi-reload</v-icon>{{ $t("basics.reload") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="!readonly && !autosave">
        <v-col :cols="12">
          <v-btn :loading="loading" large block flat class="primaryButton" @click="confirm()" ref="confirmButton"
            style="width: 100%">
            <span>{{ $t("basics.confirm") }}</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>

  <v-snackbar v-model="iCalWarningSnackbar" color="primary" :timeout="-1" location="top center">
    <div>
      <div style="display: flex; align-items: center;">
        <v-icon size="xx-large" class="mr-2">mdi-refresh</v-icon>
        <div>
          <h3>{{ $t("basics.reload") }}?</h3>
          <p>{{ $t("messages.thisCanTakeSomeMin") }}</p>
        </div>
      </div>

      <div style="display: flex; align-items: center; justify-content: space-between;">
        <v-btn color="transparent" flat @click="iCalWarningSnackbar = false">{{ $t("basics.no") }}</v-btn>
        <v-btn :loading="loading" color="transparent" flat @click="confirmReloadICal">{{ $t("basics.yes") }}</v-btn>
      </div>
    </div>
  </v-snackbar>

  <EmailChangeDrawerVue :refreshDisplay="emailEditDrawer" :userData="userData" @close="closeEditEmail" />

  <v-snackbar v-model="emailChangeSnackbar" color="primary" :timeout="2000" location="bottom right">
    <v-icon size="xx-large">mdi-email-check-outline</v-icon>
    <h3>{{ $t("basics.mailSent") }}!</h3>
  </v-snackbar>

  <AddressDrawerVue :refreshDisplay="addressEditDrawer" :readonly="readonly" :required="true"
    :addressData="autosaveFields.address" :userData="userData" :autosave="false" :cardMode="false"
    v-if="!readonly && autosaveFields.address" @close="emitClose" @refresh="emitRefresh" />

  <AddressDrawerVue :refreshDisplay="birthplaceEditDrawer" :isBirthplace="true" :required="true" :autosave="false"
    :addressData="autosaveFields.birth_place" :userData="userData" v-if="!readonly && autosaveFields.birth_place"
    :cardMode="false" @close="emitClose" @refresh="emitRefresh" />
</template>

<script>
import AddressComponentVue from "../main/AddressComponent.vue";
import AddressDrawerVue from "../main/AddressDrawer.vue";
import EmailChangeDrawerVue from "../main/EmailChangeDrawer.vue";

export default {
  props: [
    "refreshDisplay",
    "userData",
    "readonly",
    "autosave",
  ],
  emits: ["close", "refresh"],

  data() {
    return {
      autosaveFields: {},
      objectFields: ["permissions", "address"],


      roles: undefined,
      therapists: undefined,
      cities: [],

      addressEditDrawer: 0,

      birthdayPicker: false,

      loading: false,

      availUsers: [],

      iCalWarningSnackbar: false,

      genders: [
        { key: 0, name: "male" },
        { key: 1, name: "female" },
        { key: 2, name: "diverse" },
      ],

      emailEditDrawer: false,
      emailChangeSnackbar: false,

      hexColor: "rgb(0,0,0)",

      validEmailRule: (value) =>
        !value || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) || this.$i18n.t("messages.wrongEMailFormat"),
      requiredRule: (value) => (value !== undefined) || this.$i18n.t("messages.required"),
      requiredFileRule: (v) => v.length > 0 || this.$t("messages.required"),
      max256: (value) =>
        !value || value.length <= 256 || this.$i18n.t("messages.tooLong", { count: 256 }),
      max22: (value) =>
        !value || true || value.length <= 22 || this.$i18n.t("messages.tooLong", { count: 22 }),
      max20: (value) =>
        !value || value.length <= 20 || this.$i18n.t("messages.tooLong", { count: 20 }),


      requiredArrayRule: (value) =>
        (!!value && value.length > 0) || this.$i18n.t("messages.required"),
      validObjects: (value) =>
        !value ||
        this.checkArrayOfObjects(value) ||
        this.$i18n.t("messages.validSelectionrequired"),
      validObject: (value) =>
        !value ||
        typeof value == "object" ||
        this.$i18n.t("messages.validSelectionrequired"),

      showBillInfo: false
    };
  },

  components: {
    AddressComponentVue,
    AddressDrawerVue,
    EmailChangeDrawerVue
  },

  computed: {
    isTherapist() {
      if (!this.autosaveFields || !this.autosaveFields.permissions) {
        return false
      }

      for (let i = 0; i < this.autosaveFields.permissions.length; i++) {
        if (this.autosaveFields.permissions[i].therapist_role) {
          return true
        }
      }

      return false
    },
    isPatient() {
      if (!this.autosaveFields || !this.autosaveFields.permissions) {
        return false
      }

      for (let i = 0; i < this.autosaveFields.permissions.length; i++) {
        if (this.autosaveFields.permissions[i].name == "patient") {
          return true
        }
      }

      return false
    },
    isMobile() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return true;
        case "sm":
          return true;
        case "md":
          return false;
        case "lg":
          return false;
        case "xl":
          return false;
        case "xxl":
          return false;
        default:
          return false;
      }
    },
    bigCols() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 12;
        case "sm":
          return 12;
        case "md":
          return 12;
        case "lg":
          return 12;
        case "xl":
          return 6;
        case "xxl":
          return 6;
        default:
          return 6;
      }
    },
    smallCols() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 12;
        case "sm":
          return 12;
        case "md":
          return 6;
        case "lg":
          return 6;
        case "xl":
          return 6;
        case "xxl":
          return 6;
        default:
          return 6;
      }
    },
    verySmallCols() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 6;
        case "sm":
          return 6;
        case "md":
          return 6;
        case "lg":
          return 4;
        case "xl":
          return 4;
        case "xxl":
          return 4;
        default:
          return 4;
      }
    },
  },

  watch: {
    refreshDisplay() {
      this.setValues();
    },
  },

  methods: {
    gotoUser(event, user, newTab) {
      event.stopPropagation()
      event.preventDefault()

      if (newTab) {
        window.open("/backoffice/users/" + user.id + "/overview", "_blank")
      }
      else {
        this.$router.push("/backoffice/users/" + user.id + "/overview")
      }
    },
    showEditEmail() {
      this.emailEditDrawer++
    },
    closeEditEmail() {
      this.emailEditDrawer = 0
      this.emailChangeSnackbar = true
    },
    showICalWarning() {
      this.iCalWarningSnackbar++
    },
    confirmReloadICal() {
      this.loading = true
      this.axios.post(this.axios.defaults.baseURL + "calendar/ical/refresh/")
        .then(() => {
          this.loading = false
          this.iCalWarningSnackbar = false
        })
        .catch((e) => {
          console.log(e)
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
          this.loading = false
          this.iCalWarningSnackbar = false
        })
    },
    checkArrayOfObjects(array) {
      for (let i = 0; i < array.length; i++) {
        if (typeof array[i] != "object") {
          return false
        }
      }

      return true
    },
    updateColorCode(val) {
      if (!val) {
        return
      }

      this.autosaveFields.color_code = this.rgbStringToHex(val)
      this.saveFields()
    },
    rgbStringToHex(rgbString) {
      if (!rgbString) {
        return null;
      }
      const match = rgbString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
      if (!match) return null;

      const [, r, g, b] = match.map(Number);
      return (
        "#" +
        [r, g, b]
          .map((x) => {
            const hex = x.toString(16);
            return hex.length === 1 ? "0" + hex : hex;
          })
          .join("")
      );
    },
    hexToRgbString(hex) {
      if (!hex) return
      // Remove leading # if present
      hex = hex.replace(/^#/, "");

      // Handle shorthand hex (#abc)
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((c) => c + c)
          .join("");
      }

      if (hex.length !== 6) return null;

      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);

      return `rgb(${r}, ${g}, ${b})`;
    },
    showEditAddress() {
      this.addressEditDrawer++;
    },
    getReadableDate(date) {
      if (date) return this.$moment(date).format("DD.MM.YYYY");
      else return undefined;
    },
    setValues() {
      this.showNotLastName = false;

      if (this.userData) {
        this.autosaveFields = JSON.parse(JSON.stringify(this.userData));

        if (!this.autosaveFields.address) {
          this.autosaveFields.address = {
            city: {
              country: undefined,
            },
            street: undefined,
            housing_number: undefined,
            additional: undefined,
          };
        }


        if (this.userData.birthday) {
          this.autosaveFields.birthday = this.autosaveFields.birthday;
        }

        if (!this.autosaveFields.address.city) {
          this.autosaveFields.address.city = { name: "" }
        }

        this.hexColor = this.hexToRgbString(this.userData.color_code)
      } else {
        this.autosaveFields = {
          address: {
            city: {
              country: undefined,
            },
            street: undefined,
            housing_number: undefined,
            additional: undefined,
          },

          permissions: [],
          therapists: [],

          phone: undefined,
          gender: undefined,

          birthday: undefined,
        };

        this.hexColor = "rgb(0,0,0)"

        for (let i = 0; i < this.roles.length; i++) {
          if (this.roles[i].name == "patient") {
            this.autosaveFields.permissions = [this.roles[i]]
          }
        }

        for (let i = 0; i < this.therapists.length; i++) {
          if (this.therapists[i].full_name.includes(this.$store.state.name)) {
            this.autosaveFields.therapists = [this.therapists[i]]
          }
        }
      }

      setTimeout(() => {
        this.$refs.userEditForm.resetValidation();
      }, 100);
    },
    async saveGender(value) {
      if (!this.autosave || this.readonly) {
        return;
      }

      const { valid } = await this.$refs.userEditForm.validate();

      if (valid) {
        let formData = {
          id: this.userData.id,
          gender: value.key
        }

        this.patchData(formData);
      }
    },
    async savePaidBy(value) {
      if (!this.autosave || this.readonly) {
        return;
      }

      if (!this.$checkFunctionPermission(["full_user_patch"])) {
        return
      }

      const { valid } = await this.$refs.userEditForm.validate();

      if (valid) {
        let formData = {
          id: this.userData.id,
          paid_by: value.id
        }

        this.patchData(formData);
      }
    },
    async saveFields() {
      if (!this.autosave || this.readonly) {
        return;
      }

      let formData = {};
      let needsPatching = false;
      for (const [key, value] of Object.entries(this.autosaveFields)) {
        if (key == "address") {
          continue;
        }

        if (
          typeof this.autosaveFields[key] == "object" ||
          this.objectFields.indexOf(key) >= 0
        ) {
          if (typeof this.autosaveFields[key] != "object") {
            continue;
          }

          if (key.endsWith("date") || key.endsWith("day")) {
            if (
              this.$moment(this.autosaveFields[key]).format("YYYY-MM-DD") !=
              this.$moment(this.userData[key]).format("YYYY-MM-DD")
            ) {
              formData[key] = this.$moment(this.autosaveFields[key]).format("YYYY-MM-DD");
              needsPatching = true;
            }
          } else if (!this.autosaveFields[key] && !this.userData[key]) {
            continue;
          } else if (!this.autosaveFields[key] && this.userData[key]) {
            formData[key] = null;
            needsPatching = true;
          } else if (this.autosaveFields[key] && !this.userData[key]) {
            formData[key] = this.autosaveFields[key].id;
            needsPatching = true;
          } else if (this.autosaveFields[key].id != this.userData[key].id) {
            formData[key] = this.autosaveFields[key].id;
            needsPatching = true;
          }
        } else {
          if (this.autosaveFields[key] != this.userData[key]) {
            formData[key] = this.autosaveFields[key];
            needsPatching = true;
          }
        }
      }

      if (!needsPatching) {
        return;
      }

      const { valid } = await this.$refs.userEditForm.validate();

      if (formData.email) {
        if (!this.$checkFunctionPermission(['full_user_patch'])) {
          return
        }
      }

      if (valid) {
        formData["id"] = this.userData.id;
        this.patchData(formData);
      }
    },
    emitClose() {
      this.$emit("close", 1);

      this.addressEditDrawer = 0;
    },
    emitRefresh() {
      this.$emit("refresh", 1);
    },
    async confirm() {
      const { valid } = await this.$refs.userEditForm.validate();

      if (valid) {
        this.loading = true
        let formData = {};

        formData.first_name = this.autosaveFields.first_name;
        formData.last_name = this.autosaveFields.last_name;
        formData.is_company_account = this.autosaveFields.is_company_account;
        formData.paid_by = this.autosaveFields.paid_by;

        if (this.autosaveFields.gender) {
          formData.gender = this.autosaveFields.gender.key;
        }

        formData.birthday = this.$moment(this.autosaveFields.birthday).format(
          "YYYY-MM-DD"
        );
        formData.email = this.autosaveFields.email;
        formData.phone = this.autosaveFields.phone;
        formData.address = this.autosaveFields.address;

        formData.solo_bill = this.autosaveFields.solo_bill;
        formData.comment_internal = this.autosaveFields.comment_internal;


        if (this.autosaveFields.permissions) {
          formData.permissions = [];
          for (let i = 0; i < this.autosaveFields.permissions.length; i++) {
            formData.permissions.push(this.autosaveFields.permissions[i].key);
          }
        }
        if (this.autosaveFields.therapists) {
          formData.therapists = [];
          for (let i = 0; i < this.autosaveFields.therapists.length; i++) {
            formData.therapists.push(this.autosaveFields.therapists[i].id);
          }
        }


        formData.color_code = this.autosaveFields.color_code;
        formData.active_patient = this.autosaveFields.active_patient;

        if (this.userData) {
          formData.id = this.userData.id;
          this.patchData(formData);
        } else {
          this.postData(formData);
        }
      }
    },
    fetchRoles() {
      return new Promise((resolve, reject) => {
        this.axios
          .get(this.axios.defaults.baseURL + "user/roles/?show_all=1")
          .then((resp) => {
            this.roles = resp.data;

            resolve();
          })
          .catch((e) => {
            console.log(e);
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
            resolve();
          });
      });
    },
    fetchUsers(searchQ, therapistsOnly) {
      return new Promise((resolve, reject) => {
        let query = ""
        if (therapistsOnly) {
          query = "?therapists=1"
        }
        else {
          query = "?therapists=0"
        }

        if (searchQ) {
          query += "&query=" + searchQ
        }

        this.axios
          .get(this.axios.defaults.baseURL + "user/users/" + query)
          .then((resp) => {
            if (therapistsOnly) {
              this.therapists = resp.data.results;
            }
            else {
              this.availUsers = resp.data.results
            }
            resolve();
          })
          .catch((e) => {
            console.log(e);
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
            resolve();
          });
      });
    },
    savePermissions() {
      if (!this.autosave || this.readonly) {
        return;
      }

      let permissions = [];

      if (this.autosaveFields.permissions) {
        for (let i = 0; i < this.autosaveFields.permissions.length; i++) {
          permissions.push(this.autosaveFields.permissions[i].key);
        }
      }

      let formData = {
        id: this.userData.id,
        permissions: permissions,
      };

      this.patchData(formData);
    },
    saveTherapists() {
      if (!this.autosave || this.readonly) {
        return;
      }

      let therapists = [];

      if (this.autosaveFields.therapists) {
        for (let i = 0; i < this.autosaveFields.therapists.length; i++) {
          therapists.push(this.autosaveFields.therapists[i].id);
        }
      }

      let formData = {
        id: this.userData.id,
        therapists: therapists,
      };

      this.patchData(formData);
    },
    postData(formData) {
      let url = "user/users/post/";

      this.axios
        .post(this.axios.defaults.baseURL + url, formData)
        .then((resp) => {
          this.$emit("close", 1);
          this.loading = false
        })
        .catch((e) => {
          console.log(e);
          this.loading = false
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    },

    patchData(formData) {
      if (!this.autosave && !formData.id) {
        return;
      }

      let url = "user/users/patch/";

      this.axios
        .patch(this.axios.defaults.baseURL + url + formData.id, formData)
        .then((resp) => {

          if (this.autosave) {
            window.dispatchEvent(new CustomEvent("showSaveDisplay"));

            this.loading = false
            this.$emit("refresh", 1);
          }
          else {

            this.$emit("close", 1);
          }
        })
        .catch((e) => {
          console.log(e);
          this.loading = false
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    },
    fetchData() {
      this.fetchRoles().then(() => {
        this.fetchUsers(undefined, true).then(() => {
          this.setValues();

          if (this.$checkFunctionPermission(["full_user_patch"])) {
            this.fetchUsers()
          }
        })
      })
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>
